import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react'
import { PopoverContentType } from './FloatingInspectorButton'
import uniqueId from '@/utils/unique'
import FloatingTooltip from './SharedComponents/Tooltip/FloatingTooltip'

export interface WithPopoverHandle {
  closePopover: () => void
  togglePopover: () => void
}

interface WithPopoverProps {
  popoverContent: PopoverContentType
  children: React.ReactNode | ((ctx: { isOpen: boolean }) => React.ReactNode)
  id?: string
  onOpenChange?: (isOpen: boolean) => void
  onToggle?: () => void
  /** When set, a tooltip is shown on hover and automatically hidden while the popover is open. */
  tooltipText?: string
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right'
  /** Stretches the anchor so full-width triggers (inspector rows) fill their column. */
  fullWidth?: boolean
}

/**
 * WithPopover wraps any children and manages popover open/close state.
 * It renders the popover via popoverContent when open, positioned relative to
 * the wrapper div. Use onOpenChange to react to open/close in the parent.
 * Call togglePopover (via ref) to open/close; onToggle is invoked on each toggle.
 */
const WithPopover = forwardRef<WithPopoverHandle, WithPopoverProps>(
  ({ popoverContent, children, id, onOpenChange, onToggle, tooltipText, tooltipPlacement = 'top', fullWidth = false }, ref) => {
    const uid = useRef(uniqueId())
    const anchorRef = useRef<HTMLDivElement>(null)
    const [showPopover, setShowPopover] = useState(false)
    const [isHover, setIsHover] = useState(false)
    const showPopoverRef = useRef(showPopover)
    showPopoverRef.current = showPopover

    // Keep callback refs fresh so imperative handles always call the latest props
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange
    const onToggleRef = useRef(onToggle)
    onToggleRef.current = onToggle

    const setOpen = useCallback((next: boolean) => {
      showPopoverRef.current = next
      setShowPopover(next)
      if (!next) setIsHover(false)
      onOpenChange?.(next)
    }, [onOpenChange])

    useImperativeHandle(ref, () => ({
      closePopover: () => {
        setShowPopover(false)
        setIsHover(false)
        onOpenChangeRef.current?.(false)
      },
      togglePopover: () => {
        const next = !showPopoverRef.current
        setShowPopover(next)
        onOpenChangeRef.current?.(next)
        onToggleRef.current?.()
      },
    }), [])

    useEffect(() => {
      if (!showPopover) return
      // mousedown, not click: a drag gesture (e.g. dragging a slider inside the
      // popover content) can release with the pointer well outside the popover's
      // rendered bounds — that 'click' would target an unrelated element and
      // wrongly read as "outside". mousedown fires where the gesture STARTS,
      // which is reliably inside whatever the user is actually interacting with;
      // popover content that wants to be exempt (e.g. WrapPopover) stops this
      // event from ever reaching here.
      function handleMouseDownOutside(event: MouseEvent) {
        if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) {
          setOpen(false)
        }
      }
      function handleEscape(event: KeyboardEvent) {
        if (event.key !== 'Escape') return
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
      }
      document.addEventListener('mousedown', handleMouseDownOutside)
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handleMouseDownOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }, [setOpen, showPopover])

    const closePopover = () => setOpen(false)
    const buttonRef = anchorRef as unknown as React.RefObject<HTMLElement>

    return (
      <div
        id={id ?? uid.current}
        ref={anchorRef}
        style={fullWidth
          ? { display: 'flex', position: 'relative', width: '100%' }
          : { display: 'inline-flex', position: 'relative' }}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
      >
        {typeof children === 'function' ? children({ isOpen: showPopover }) : children}
        {showPopover && popoverContent(buttonRef, closePopover)}
        {tooltipText && isHover && !showPopover && (
          <FloatingTooltip tooltipText={tooltipText} placement={tooltipPlacement} />
        )}
      </div>
    )
  }
)

WithPopover.displayName = 'WithPopover'
export default WithPopover

export type { WithPopoverProps }
